import clsx from 'clsx';
import React, { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import TooltipCrossIcon from '../../assets/icons/tooltip-cross-icon.svg?react';
import ModalComponent from '../../components/ModalComponent';
import ChevronLeftIcon from '../../assets/icons/chevron-down-icon.svg?react';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
// import DownloadIcon from '../../assets/icons/download-icon.svg?react';
import Button from '../../components/Button';
import { toggleModal } from '../../reducers/modalToggle';
import { useNavigate } from 'react-router';
import {
  Attachments,
  CorrectiveActionsStatusEnum,
  GeneralSuccessResponse,
  GetIncidentByIdResponse,
  IncidentsStatusEnum,
  LoginResponseData,
} from '@EHSNavigator/backend';
import { useMutation } from '@tanstack/react-query';
import { useApiClients } from '../../client/api/useApiClient';
import { toast } from 'react-toastify';
import moment from 'moment';
import CorrectiveActionBanner from '../../components/CorrectiveActionBanner';
import ChangesHistory from '../../components/ChangesHistory';
import { useLocalStorage } from '@uidotdev/usehooks';
import AttachmentPreviewModal from '../../components/AttachmentPreviewModal';
interface ReportedIncidentsProps {
  otherClasses?: string;
  data?: GetIncidentByIdResponse['incident'];
}

const ReportedIncidents: React.FC<ReportedIncidentsProps> = ({
  otherClasses,
  data,
}) => {
  const reportedIncidentsClasses = clsx(otherClasses);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userInfo] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );
  const [selectedAttachment, setSelectedAttachment] =
    useState<Attachments | null>(null);
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );
  const buttonSize = useButtonSizeLarge();
  const { IncidentReportingApi } = useApiClients();

  const withdrawIncidentMutation = useMutation<
    GeneralSuccessResponse,
    Error,
    { id: string }
  >({
    mutationFn: async ({ id }) => {
      const response =
        await IncidentReportingApi.incidentReportingControllerWithdrawIncident({
          id,
        });
      return response?.data;
    },
  });

  const handleWithdrawClick = (
    id: GetIncidentByIdResponse['incident']['id'] | undefined,
  ) => {
    if (!id) return;
    withdrawIncidentMutation.mutate(
      { id },
      {
        onSuccess: (data) => {
          dispatch(toggleModal(''));
          toast.success(data?.message);
          navigate(-1);
        },
        onError: (error: any) => {
          const errorMessage = error.response.data.message;
          if (Array.isArray(errorMessage)) {
            errorMessage.forEach((msg: string) => toast.error(msg));
          } else {
            toast.error(errorMessage || 'An unexpected error occurred.');
          }
        },
      },
    );
  };

  const correctiveAction =
    ((data?.correctiveActions || [])?.length > 0 &&
      data?.correctiveActions?.find(
        (node) => node?.status === CorrectiveActionsStatusEnum.Active,
      )) ||
    null;

  return (
    <div className={reportedIncidentsClasses} data-testid="reported-incidents">
      <div>
        <Button
          href="/my-reported-incidents"
          variant="link"
          otherClasses="gap-2.5 w-fit mb-15"
        >
          <span className="bg-brand-byzantine rounded-5 flex h-8 w-8 items-center justify-center">
            <ChevronLeftIcon className="h-7 w-7 rotate-90 [&>path]:fill-white" />
          </span>
          <strong>Return</strong>
        </Button>
        <h1 className="h3 mb-1 capitalize lg:mb-0">{data?.title}</h1>
        <div className="mt-15 flex flex-wrap gap-15">
          <p className="text-neutral-tones-text/80 font-inter-variable -tracking-form-label lg:-tracking-button flex items-center gap-2.5 text-sm leading-5 lg:gap-15 lg:text-xl lg:leading-6">
            Status
            <span
              className={clsx(
                'font-bold uppercase',
                data?.status === IncidentsStatusEnum?.Active
                  ? 'text-brand-posh-pink'
                  : data?.status === IncidentsStatusEnum?.Closed
                    ? 'text-brand-papery'
                    : 'text-brand-byzantine',
              )}
            >
              {data?.status}
            </span>
          </p>
          {data?.incidentRefId && (
            <p className="text-neutral-tones-text/80 font-inter-variable -tracking-form-label lg:-tracking-button border-l-neutral-tones-text/10 flex items-center gap-2.5 border-l pl-15 text-sm leading-5 lg:gap-15 lg:text-xl lg:leading-6">
              Reference ID
              <span className="text-neutral-tones-text/50">
                {data?.incidentRefId}
              </span>
            </p>
          )}
          <p className="text-neutral-tones-text/80 font-inter-variable -tracking-form-label lg:-tracking-button flex items-center gap-2.5 text-sm leading-5 lg:gap-15 lg:text-xl lg:leading-6">
            Reported by
            <span className="text-brand-byzantine font-bold capitalize">
              {data?.user?.fullName}{' '}
            </span>
          </p>
        </div>
      </div>
      {data?.reOpenComment && (
        <>
          <hr className="border-neutral-tones-text/10 mt-6 mb-5 lg:mt-15" />
          <div className="rounded-10 bg-brand-posh-pink p-15">
            <h6 className="font-inter-variable mb-15 flex items-center gap-15 text-lg font-bold text-white lg:text-xl">
              <WarningIcon className="h-6 min-h-6 w-6 min-w-6 lg:h-8 lg:w-8 [&>g>path]:fill-current" />
              This Incident was reopened by{' '}
              {data?.reOpenComment?.user?.fullName}.
            </h6>
            <p className="font-inter-variable mb-1 text-xs leading-4 font-bold text-white lg:text-sm">
              REMARKS
            </p>
            <p className="font-inter-variable text-form-label font-normal text-white italic lg:text-base lg:leading-5">
              {data?.reOpenComment?.comment}
            </p>
          </div>
          {correctiveAction && (
            <hr className="border-neutral-tones-text/10 mt-6 mb-6" />
          )}
        </>
      )}
      {correctiveAction && (
        <CorrectiveActionBanner
          otherClasses={clsx(data?.reOpenComment && '!mt-0')}
          correctiveAction={correctiveAction}
          baseUrl={
            data?.user?.id === userInfo?.user?.id
              ? '/assigned-to-me'
              : '/all-corrective-actions'
          }
        />
      )}
      <div className="border-t-neutral-tones-text/10 mt-6 border-t py-6 lg:mt-9 lg:py-9">
        <div className="grid max-w-950 grid-cols-1 items-start gap-9 md:grid-cols-2 md:gap-12 lg:gap-100">
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold lg:mb-25 lg:text-xl lg:leading-6">
              Date & Time
            </h6>
            {data?.dateUndetermined ? (
              <div className="flex items-start gap-75 md:gap-12 lg:gap-75">
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                    Date & Time
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                    Date & Time Undetermined
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-75 md:gap-12 lg:gap-75">
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                    Date
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                    {moment(data?.incidentDate).format('MMMM D, YYYY')}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                    Time
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                    {moment(data?.incidentTime, 'HH:mm:ss').format('h:mm A')}
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold lg:mb-25 lg:text-xl lg:leading-6">
              Location of Incident
            </h6>
            <div className="flex items-start gap-75">
              <div className="flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Location Address
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal capitalize">
                  {data?.location}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="border-y-neutral-tones-text/10 border-y py-6 lg:py-9">
        <div className="grid max-w-950 grid-cols-1 items-start gap-9 md:grid-cols-2 md:gap-12 lg:gap-100">
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold lg:mb-25 lg:text-xl lg:leading-6">
              Department Involved
            </h6>
            <div className="grid grid-cols-2 gap-x-75 gap-y-6 md:gap-x-12 lg:gap-x-75 lg:gap-y-[50px]">
              <div className="col-span-1 flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Department
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                  {data?.department?.name}
                </p>
              </div>
              <div className="col-span-1 flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Contractor Involvement
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                  {data?.wereContractorInvolved ? 'Yes' : 'No'}
                </p>
              </div>
              {(data?.affectedBodyParts || [])?.length > 0 && (
                <div className="col-span-2 flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                    Affected Body Parts
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                    {data?.affectedBodyParts?.join(', ')}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col">
            <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold lg:mb-25 lg:text-xl lg:leading-6">
              Severity
            </h6>
            <div className="flex flex-wrap items-start gap-x-75 gap-y-6 md:gap-12 lg:gap-75">
              <div className="flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Type
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                  {data?.incidentType?.name}
                </p>
              </div>
              <div className="flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Severity
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                  {data?.severity}
                </p>
              </div>
              <div className="flex flex-col gap-2.5">
                <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                  Were multiple people injured?
                </h6>
                <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                  {data?.wereManyInjured ? 'Yes' : 'No'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-864 py-6 lg:py-9">
        <div className="flex flex-col">
          <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold md:mb-25 lg:text-xl lg:leading-6">
            Incident Description
          </h6>
          <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
            <div className="flex flex-col gap-2.5 md:col-span-9 md:pe-8">
              <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                Description
              </h6>
              <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                {data?.description}
              </p>
            </div>
            <div className="flex flex-col gap-2.5 md:col-span-3">
              <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-xl lg:leading-6">
                Images Uploaded
              </h6>
              <div className="mt-2.5 flex flex-col gap-2">
                {data?.attachments && data.attachments.length > 0 ? (
                  data.attachments.map((attachment) => {
                    const isPreviewable =
                      attachment?.mediaType?.startsWith('image/') ||
                      attachment?.mediaType?.startsWith('video/') ||
                      attachment?.mediaType === 'application/pdf' ||
                      attachment?.mediaType ===
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                      attachment?.mediaType === 'application/msword';
                    return (
                      <Fragment key={attachment?.id}>
                        {!isPreviewable ? (
                          <a
                            target="_blank"
                            rel="noreferrer noreferrer"
                            href={attachment?.url}
                            className="font-inter-variable text-brand-byzantine -tracking-form-input cursor-pointer text-base leading-5 font-normal hover:underline"
                          >
                            {attachment.name}
                          </a>
                        ) : (
                          <button
                            onClick={() => {
                              setSelectedAttachment(attachment);
                              dispatch(toggleModal('attachment-preview-modal'));
                            }}
                            className="font-inter-variable text-brand-byzantine -tracking-form-input text-left text-base leading-5 font-normal break-words italic"
                          >
                            {attachment.name}
                          </button>
                        )}
                      </Fragment>
                    );
                  })
                ) : (
                  <div className="font-inter-variable global-transition text-md text-neutral-tones-text/50 flex w-full cursor-pointer rounded leading-6 font-medium">
                    No Attachments
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {((data?.status === IncidentsStatusEnum.Active &&
        data?.investigationStatus === 'Pending') ||
        data?.investigationStatus === null) &&
        data?.correctiveActions?.length === 0 &&
        data?.user?.id === userInfo?.user?.id && (
          <div className="flex flex-wrap items-center gap-x-15 gap-y-6">
            <Button
              size={buttonSize}
              onClick={() => dispatch(toggleModal('withdraw-report'))}
              otherClasses="bg-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink"
            >
              <TooltipCrossIcon className="[&>g>path]:fill-current" /> Withdraw
              Report
            </Button>
            {/* <Button variant="secondary" size={useButtonSizeLarge()}>
        {/* <Button variant="secondary" size={buttonSize}>
        <DownloadIcon />
        Download Report
        </Button> */}
          </div>
        )}

      {data && (data?.taskHistory || [])?.length > 0 && (
        <ChangesHistory data={data?.taskHistory} />
      )}

      {(modalName === 'withdraw-report' ||
        modalName === 'attachment-preview-modal') && (
        <ModalComponent
          otherClasses="px-4 sm:px-0 "
          additionalClass="w-full max-w-419 rounded-2xl "
        >
          {modalName === 'withdraw-report' && (
            <div className="flex w-full flex-col items-center gap-12 p-8">
              <div className="flex flex-col items-center gap-2.5 text-center">
                <TooltipCrossIcon className="[&>g>path]:fill-brand-posh-pink h-85 w-85" />
                <h4 className="text-brand-posh-pink capitalize">
                  Are you sure?
                </h4>
                <p className="font-inter-variable text-brand-posh-pink -tracking-text-area text-lg leading-5 font-normal">
                  This action is irreversible and you will not be able to
                  retrieve this report.
                </p>
              </div>
              <div className="grid w-full grid-cols-2 gap-2.5">
                <Button
                  size="smaller"
                  onClick={() => dispatch(toggleModal(''))}
                  variant="secondary"
                  otherClasses="w-full text-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink border-transparent"
                >
                  Cancel
                </Button>
                <Button
                  loading={withdrawIncidentMutation.isPending}
                  disable={withdrawIncidentMutation.isPending}
                  size="smaller"
                  onClick={() => handleWithdrawClick(data?.id)}
                  otherClasses="bg-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink"
                >
                  Withdraw
                </Button>
              </div>
            </div>
          )}

          {modalName === 'attachment-preview-modal' && (
            <AttachmentPreviewModal
              handleClose={() => {
                dispatch(toggleModal(''));
                setSelectedAttachment(null);
              }}
              attachment={selectedAttachment}
            />
          )}
        </ModalComponent>
      )}
    </div>
  );
};

export default ReportedIncidents;
